import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dynamic_attributes", "json_input", "suggestion_button", "movement_classification_input"]
  outputEvents = []

  connect() { 
    // Initialize fields when the page loads
    if (this.hasDynamic_attributesTarget) {
      // Load existing dynamic attributes from the hidden input first
      const existingAttributes = this.json_inputTarget.value
      if (existingAttributes && existingAttributes !== "null" && existingAttributes !== "{}") {
        try {
          const parsedAttributes = JSON.parse(existingAttributes)
          // Store the existing values to apply after fields are loaded
          this.existingValues = parsedAttributes
        } catch (e) {
          console.error("Error parsing existing dynamic attributes:", e)
          this.existingValues = {}
        }
      } else {
        this.existingValues = {}
      }
      
      // Fetch output events first and then initialize fields
      this.getOutputEvents().then(() => {
        // Then update fields after events are loaded
        this.initialUpdateFields()
        this.updateButtonHref() // Ensure the button has the correct value when loading
      }).catch(error => {
        console.error("Failed to load output events:", error)
      })
    } else {
      console.error("Missing dynamic_attributes target")
    }
    
    // Add form submit listener to ensure values are updated before submission
    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this))
    }
  }

  updateButtonHref() {
    const fileIndex = this.element.dataset.fileIndex || 0
    // Captura el valor del input movement_classification
    let selectedValue = this.movement_classification_inputTarget ? this.movement_classification_inputTarget.value : "";
    let button = this.suggestion_buttonTarget;
    let url = new URL(button.href);
    url.searchParams.set("movement_classification", selectedValue);
    url.searchParams.set("file_index", fileIndex);
    button.href = url.toString();
  }

  initialUpdateFields() {
    const totalFiles = parseInt(this.element.dataset.totalFiles) || 0;
    const fileIndexes = Array.from({ length: totalFiles }, (_, i) => i);    
    fileIndexes.forEach(fileIndex => {
      this.updateFields(fileIndex)
    })
    if (this.element.dataset.fileIndex) {
      this.updateFields(parseInt(this.element.dataset.fileIndex))
    }
  }

  getOutputEvents() {
    // If we already have output events, return them as a resolved promise
    if (this.outputEvents && Array.isArray(this.outputEvents) && this.outputEvents.length > 0) {
      return Promise.resolve(this.outputEvents)
    }
    
    // Otherwise fetch them from the server
    return fetch(`/document_reader/private_equity_events/output_events`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        this.outputEvents = data
        return data // Return the data for chaining
      })
      .catch(error => {
        console.error("Error fetching output events:", error)
        this.outputEvents = []
        throw error // Re-throw to allow handling in the connect method
      })
  }

  updateFields(event) {
    const fileIndex = typeof event === 'number' ? event : event.currentTarget.dataset.fileIndex
    const classificationId = this.dynamic_attributesTarget.value
    
    if (!classificationId) {
      // Hide all dynamic fields
      this.hideAllDynamicFields(fileIndex)
      return    
    }
    
    // Check if output events are loaded before proceeding
    if (!this.outputEvents) {
      console.error("Output events not loaded yet or empty")
      // Instead of failing, retry after a short delay
      setTimeout(() => this.updateFields(event), 500)
      return
    }
    
    // Find the matching event by classificationId
    const matchingEvent = this.outputEvents[classificationId];
    if (matchingEvent) {
      // Show only the relevant fields
      this.showRelevantFields(matchingEvent, fileIndex)
      // Apply existing values after fields are shown
      if (this.existingValues) {
        this.applyExistingValues(this.existingValues, fileIndex)
      }
    } else {
      console.error('Matching event not found or invalid fields data:', classificationId)
      this.hideAllDynamicFields(fileIndex)
    }
  }
  
  hideAllDynamicFields(fileIndex = this.fileIndexValue) {
    // Seleccionar todos los campos dinámicos para este índice y ocultarlos
    const dynamicFields = this.element.querySelectorAll(`[data-dynamic-field][data-file-index="${fileIndex}"]`)
    dynamicFields.forEach(field => {
      field.classList.add('hidden')
    })
    // Mostrar el botón de generar sugerencia que estaba oculto
    const generateSuggestionButton = this.element.querySelector('.dynamic-element')
    if (generateSuggestionButton) {
      generateSuggestionButton.classList.add('hidden')
    }

    const divToHide = this.element.querySelector(`#movement_classification_form[data-file-index="${fileIndex}"]`)
    if (divToHide) {
      divToHide.classList.add('hidden')
    }

    this.updateDynamicAttributesJson(fileIndex)
  }

  showRelevantFields(fields, fileIndex = this.fileIndexValue) {
    // Primero ocultar todos los campos para este índice
    this.hideAllDynamicFields(fileIndex)
    
    fields.forEach(field => {
      const fieldNames = []
      
      if (field.name) {
        fieldNames.push(field.name)
      }
      
      if (field.amount && field.amount.name) {
        fieldNames.push(field.amount.name)
      }
      if (field.quantity && field.quantity.name) {
        fieldNames.push(field.quantity.name)
      }
      
      if (fieldNames.length === 0 && typeof field === 'object') {
        const values = Object.values(field)
        values.forEach(value => {
          if (value && value.name) {
            fieldNames.push(value.name)
          }
        })
      }
      fieldNames.forEach(fieldName => {
        if (fieldName) {
          // Update selector to include fileIndex
          const fieldElement = this.element.querySelector(`[data-dynamic-field="${fieldName}"][data-file-index="${fileIndex}"]`)
          if (fieldElement) {
            fieldElement.classList.remove('hidden')
          } else {
            //console.log(`Field element not found for ${fieldName} with index ${fileIndex}`)
          }
        }
      })
    })
    
    // Mostrar el botón de generar sugerencia que estaba oculto
    const generateSuggestionButton = this.element.querySelector('.dynamic-element')
    if (generateSuggestionButton) {
      generateSuggestionButton.classList.remove('hidden')
    }

    const divToShow = this.element.querySelector(`#movement_classification_form[data-file-index="${fileIndex}"]`)
    if (divToShow) {
      divToShow.classList.remove('hidden')
    }
    
    // Actualizar el JSON después de mostrar los campos
    this.updateDynamicAttributesJson(fileIndex)
  }
  
  updateDynamicAttributesJson(fileIndex = this.fileIndexValue) {
    // Update selector to include fileIndex and scope to this.element
    const dynamicFields = this.element.querySelectorAll(`[data-dynamic-field][data-file-index="${fileIndex}"]:not(.hidden)`)
    const dynamicAttributes = {}
    dynamicFields.forEach(field => {
      const fieldName = field.dataset.dynamicField
      if (!fieldName) {
        return
      }

      // Check if any descendant element within 'field' has the tags controller attribute
      const tagsControllerElement = field.querySelector('[data-controller="tags"]');
      const isTagsField = !!tagsControllerElement; // Check if an element was found

      if (isTagsField) {
        // For tags fields, get the value from the hidden input within the tags controller element
        const hiddenInput = tagsControllerElement.querySelector('[data-tags-target="hiddenInput"]')

        if (hiddenInput) {
          try {
            const tagsValue = JSON.parse(hiddenInput.value)
            // Return just the first value instead of the array
            dynamicAttributes[fieldName] = tagsValue.length > 0 ? tagsValue[0] : null
          } catch (e) {
            dynamicAttributes[fieldName] = null
          }
        } else {
          // Fallback to data attribute on the tags controller element if hidden input not found
          const tagsValue = tagsControllerElement.getAttribute('data-tags-tags-value')
          if (tagsValue) {
            try {
              const parsedValue = JSON.parse(tagsValue)
              dynamicAttributes[fieldName] = parsedValue.length > 0 ? parsedValue[0] : null
            } catch (e) {
              console.error(`Error parsing tags value from attribute for ${fieldName}:`, e)
              dynamicAttributes[fieldName] = null
            }
          } else {
            dynamicAttributes[fieldName] = null
          }
        }
      } else {
        // For regular fields - look for any input within the field container
        const input = field.querySelector('input, select, textarea')
        if (input) {
          dynamicAttributes[fieldName] = input.value
        } else {          
          // Try to find the input with a name that contains the fieldName
          const namePattern = new RegExp(`\\[${fieldName}\\]`)
          const inputByName = field.querySelector(`input[name*="${fieldName}"], select[name*="${fieldName}"], textarea[name*="${fieldName}"]`)
          
          if (inputByName) {
            dynamicAttributes[fieldName] = inputByName.value
          } else {
            console.log(`No input found by name pattern for ${fieldName}`)
          }
        }
      }
    })
    this.json_inputTarget.value = JSON.stringify(dynamicAttributes)
  }

  // Modify the tagChanged method to be more robust
  tagChanged(event) {
    this.updateDynamicAttributesJson()
  }

  // Add a new method to handle form submission
  onFormSubmit(event) {
    this.updateDynamicAttributesJson()
  }
  
  // Apply existing values to fields
  applyExistingValues(values, fileIndex = this.fileIndexValue) {    
    Object.entries(values).forEach(([key, value]) => {
      // Update selector to include fileIndex and scope to this.element
      const fieldElement = this.element.querySelector(`[data-dynamic-field="${key}"][data-file-index="${fileIndex}"]`)
      if (!fieldElement) {
        return
      }
      
      // Check if this is a tags field
      const isTagsField = fieldElement.hasAttribute('data-controller') && fieldElement.getAttribute('data-controller') === 'tags'
      
      if (isTagsField) {
        // Handle tags field differently
        const tagsValue = Array.isArray(value) ? value : [value]
        
        // Update the data attribute
        fieldElement.setAttribute('data-tags-tags-value', JSON.stringify(tagsValue))
        
        // Update the hidden input for form submission
        const hiddenInput = fieldElement.querySelector('[data-tags-target="hiddenInput"]')
        if (hiddenInput) {
          hiddenInput.value = JSON.stringify(tagsValue)
          // Trigger a change event to ensure our controller picks up the change
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
        }
      } else {
        // Try to find the input with a name that contains the key
        const namePattern = new RegExp(`\\[${key}\\]`)
        const input = fieldElement.querySelector(`input[name*="${key}"], select[name*="${key}"], textarea[name*="${key}"]`)
        
        if (input) {
          input.value = value
        } else {
          console.log(`Input not found for ${key}`)
        }
      }
    })
    
    // Update the JSON input after applying values
    this.updateDynamicAttributesJson(fileIndex)
  }
}